import { Center, Loader } from '@mantine/core';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
// Shared components and contexts
import { ErrorBoundary } from './shared/components';
import { FeatureToggleProvider } from './shared/contexts';
import { CookieConsentBanner } from './shared/components/CookieConsent';

// Auth feature
import { AuthProvider, useAuth, AuthCallback, Login, Signup, ResetPassword } from './features/auth';

// Calculator features
import { Calculator } from './features/calculator';
import { ProCalculator } from './features/pro-calculator';
import { SharedCalculation } from './features/shared-calculation';
import { ProsumerCalculator } from './features/prosumer-calculator';

// Dashboard and import features
import { Dashboard } from './features/dashboard';
import { HourlyImport } from './features/hourly-import';

// News feature
import { News, NewsArticle } from './features/news';

// Guide feature
import { Guide, GuideArticle } from './features/guide';

// Energy Certificate Simulator
import { EnergyCertificateSimulator } from './features/energy-certificate-simulator';

// OSD Components
import { OSDLayout } from './components/Layout';
import { OSDHomepage } from './pages/OSDHomepage';

// Pages that remain in pages/ folder
import { Contact } from './pages/Contact';
import { FAQ } from './pages/FAQ';
import { Landing } from './pages/Landing';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { Tariffs } from './pages/Tariffs';
import { Terms } from './pages/Terms';
import { ConsumptionPatterns } from './pages/ConsumptionPatterns';
import { G14PriceImport } from './pages/G14PriceImport';
import { QuickG14Calculator } from './features/g14-calculator/QuickG14Calculator';
import { TariffG13sPage } from './pages/TariffG13sPage';
import { TariffG14Page } from './pages/TariffG14Page';

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated
 * Preserves OSD context in redirect URL
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const params = useParams<{ osd?: string }>();

  // Extract OSD from URL params, default to TAURON
  const osd = params.osd || 'TAURON';

  // Show loader while checking authentication status
  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  // Redirect to login with OSD prefix if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={`/${osd}/login`} replace />;
  }

  return <>{children}</>;
}

/**
 * TAURON-Only Route Wrapper
 * Pro Calculator is only available for TAURON users.
 * Non-TAURON users are redirected to the basic calculator.
 */
function TauronOnlyRoute({ children }: { children: React.ReactNode }) {
  const params = useParams<{ osd?: string }>();
  const osd = params.osd?.toUpperCase() || 'TAURON';

  // Only TAURON users can access this route
  if (osd !== 'TAURON') {
    return <Navigate to={`/${osd}/kalkulatory/podstawowy`} replace />;
  }

  return <>{children}</>;
}

/**
 * Legacy Redirect Components
 * Handle parameterized route redirects with slug preservation
 * All legacy routes redirect to TAURON (default OSD)
 */
function LegacyGuideArticleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/TAURON/przewodnik/analizy/${slug}`} replace />;
}

function LegacyGuideArticleRedirectWithOSD() {
  const { slug } = useParams();
  return <Navigate to={`/TAURON/przewodnik/analizy/${slug}`} replace />;
}

function LegacyNewsArticleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/TAURON/aktualnosci/${slug}`} replace />;
}

/**
 * App Routes Component
 * Must be inside AuthProvider to access auth context
 */
function AppRoutes() {
  return (
    <Routes>
      {/* OSD Selection Homepage - First screen */}
      <Route path="/" element={<OSDHomepage />} />

      {/* Auth Routes - Support both global and OSD-prefixed */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/:osd/login" element={<Login />} />
      <Route path="/:osd/signup" element={<Signup />} />
      <Route path="/:osd/reset-password" element={<ResetPassword />} />

      {/* Shared Calculation Routes - Support both with and without OSD */}
      <Route path="/calc/:year/:osd/:hash" element={<SharedCalculation />} />
      <Route path="/calc/:year/:hash" element={<SharedCalculation />} />

      {/* Global Pages (no OSD prefix) */}
      <Route path="/kontakt" element={<Contact />} />
      {/* Legacy redirect: /contact → /kontakt */}
      <Route path="/contact" element={<Navigate to="/kontakt" replace />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />

      {/* OSD-scoped Routes - All application features under /:osd/* */}
      <Route path="/:osd/*" element={<OSDLayout />}>
        {/* Landing page for specific OSD */}
        <Route index element={<Landing />} />

        {/* Przewodnik Section */}
        <Route path="przewodnik/taryfy" element={<Tariffs />} />
        <Route path="przewodnik/wzorce" element={<ConsumptionPatterns />} />
        <Route path="przewodnik/analizy" element={<Guide />} />
        <Route path="przewodnik/analizy/:slug" element={<GuideArticle />} />
        <Route path="przewodnik/faq" element={<FAQ />} />

        {/* Kalkulatory Section */}
        <Route path="kalkulatory/podstawowy" element={<Calculator />} />
        <Route path="kalkulatory/podstawowy/:tariff" element={<Calculator />} />
        {/* Pro Calculator - TAURON only (requires hourly data import) */}
        <Route
          path="kalkulatory/zaawansowany"
          element={
            <TauronOnlyRoute>
              <ProCalculator />
            </TauronOnlyRoute>
          }
        />
        {/* Prosumer Calculator - TAURON only, auth required */}
        <Route
          path="kalkulatory/prosument"
          element={
            <ProtectedRoute>
              <TauronOnlyRoute>
                <ProsumerCalculator />
              </TauronOnlyRoute>
            </ProtectedRoute>
          }
        />
        {/* Quick G14 Calculator - Public, TAURON only */}
        <Route
          path="kalkulatory/g14-szybki"
          element={
            <TauronOnlyRoute>
              <QuickG14Calculator />
            </TauronOnlyRoute>
          }
        />

        {/* Dedicated Tariff Landing Pages - SEO optimized */}
        <Route path="kalkulatory/podstawowy/G13s" element={<TariffG13sPage />} />
        <Route path="kalkulatory/podstawowy/G14" element={<TariffG14Page />} />

        {/* Narzędzia Section */}
        <Route path="symulator-certyfikatu" element={<EnergyCertificateSimulator />} />

        {/* Aktualności Section */}
        <Route path="aktualnosci" element={<News />} />
        <Route path="aktualnosci/:slug" element={<NewsArticle />} />

        {/* Konto Section (Protected) */}
        <Route
          path="konto/panel"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="konto/import"
          element={
            <ProtectedRoute>
              <HourlyImport />
            </ProtectedRoute>
          }
        />
        <Route
          path="g14-import"
          element={
            <ProtectedRoute>
              <G14PriceImport />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Legacy Redirects - Redirect to TAURON (default OSD) */}
      {/* Przewodnik Section */}
      <Route path="/tariffs" element={<Navigate to="/TAURON/przewodnik/taryfy" replace />} />
      <Route path="/przewodnik/taryfy" element={<Navigate to="/TAURON/przewodnik/taryfy" replace />} />
      <Route path="/consumption-patterns" element={<Navigate to="/TAURON/przewodnik/wzorce" replace />} />
      <Route path="/przewodnik/wzorce" element={<Navigate to="/TAURON/przewodnik/wzorce" replace />} />
      <Route path="/guide" element={<Navigate to="/TAURON/przewodnik/analizy" replace />} />
      <Route path="/przewodnik/analizy" element={<Navigate to="/TAURON/przewodnik/analizy" replace />} />
      <Route path="/guide/:slug" element={<LegacyGuideArticleRedirect />} />
      <Route path="/przewodnik/analizy/:slug" element={<LegacyGuideArticleRedirectWithOSD />} />
      <Route path="/faq" element={<Navigate to="/TAURON/przewodnik/faq" replace />} />
      <Route path="/przewodnik/faq" element={<Navigate to="/TAURON/przewodnik/faq" replace />} />

      {/* Kalkulatory Section */}
      <Route path="/calculator" element={<Navigate to="/TAURON/kalkulatory/podstawowy" replace />} />
      <Route path="/kalkulatory/podstawowy" element={<Navigate to="/TAURON/kalkulatory/podstawowy" replace />} />
      <Route path="/kalkulatory/podstawowy/:osd" element={<Navigate to="/TAURON/kalkulatory/podstawowy" replace />} />
      <Route path="/kalkulatory/podstawowy/:osd/:tariff" element={<Navigate to="/TAURON/kalkulatory/podstawowy" replace />} />
      <Route path="/pro-calculator" element={<Navigate to="/TAURON/kalkulatory/zaawansowany" replace />} />
      <Route path="/kalkulatory/g14-szybki" element={<Navigate to="/TAURON/kalkulatory/g14-szybki" replace />} />
      <Route path="/kalkulatory/zaawansowany" element={<Navigate to="/TAURON/kalkulatory/zaawansowany" replace />} />
      <Route path="/kalkulatory/prosument" element={<Navigate to="/TAURON/kalkulatory/prosument" replace />} />

      {/* Aktualności Section */}
      <Route path="/news" element={<Navigate to="/TAURON/aktualnosci" replace />} />
      <Route path="/aktualnosci" element={<Navigate to="/TAURON/aktualnosci" replace />} />
      <Route path="/news/:slug" element={<LegacyNewsArticleRedirect />} />

      {/* Konto Section */}
      <Route path="/dashboard" element={<Navigate to="/TAURON/konto/panel" replace />} />
      <Route path="/konto/panel" element={<Navigate to="/TAURON/konto/panel" replace />} />
      <Route path="/hourly-import" element={<Navigate to="/TAURON/konto/import" replace />} />
      <Route path="/konto/import" element={<Navigate to="/TAURON/konto/import" replace />} />
      <Route path="/g14-import" element={<Navigate to="/TAURON/g14-import" replace />} />

      {/* Narzędzia Section */}
      <Route path="/symulator-certyfikatu" element={<Navigate to="/TAURON/symulator-certyfikatu" replace />} />

      {/* 404 - Redirect to OSD selection homepage */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Main App Component
 * Wraps the app with ErrorBoundary, AuthProvider and BrowserRouter
 */
function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <FeatureToggleProvider>
            <AppRoutes />
            {/* Cookie Consent Banner - GDPR/RODO Compliance */}
            <CookieConsentBanner />
          </FeatureToggleProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
